import { useState, useMemo, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import OrderModal from "@/components/OrderModal";
import VerifyDialog from "@/components/VerifyDialog";
import Logo from "@/components/Logo";
import { CATEGORIES } from "@/data/products";
import { useProducts } from "@/hooks/useProducts";
import { useCategoryImages, resolveCategoryImage } from "@/hooks/useCategoryImages";
import { photoUrl } from "@/utils/photoUrl";
import { useCart, useCartCount } from "@/hooks/useCart";
import { useReviewSummary } from "@/hooks/useReviewSummary";
import { useT } from "@/i18n/LanguageContext";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Search, Shield, ShoppingBag, User,
  ShoppingCart, X, AlertTriangle, Star,
} from "lucide-react";

export default function Products() {
  const navigate = useNavigate();
  const t = useT();
  const [params, setParams] = useSearchParams();
  const initialQ = params.get("q") || "";
  const initialCat = params.get("cat") || null;
  const excludeCat = params.get("not") || null;

  const [orderProduct, setOrderProduct] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState(initialCat);
  const [searchQuery, setSearchQuery] = useState(initialQ);
  const productsRef = useRef(null);
  const PRODUCTS = useProducts();
  const categoryImages = useCategoryImages();

  useEffect(() => {
    const next = {};
    if (searchQuery) next.q = searchQuery;
    if (activeCategory) next.cat = activeCategory;
    if (excludeCat) next.not = excludeCat;
    setParams(next, { replace: true });
  }, [searchQuery, activeCategory, excludeCat, setParams]);

  const openOrder = (product) => { setOrderProduct(product); setModalOpen(true); };
  const { addItem } = useCart();
  const cartCount = useCartCount();
  const handleAddToCart = (product) => {
    addItem(product);
    toast.success(`${product.name} · ${t("prod.added_to_cart")}`);
  };

  const visibleProducts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const inCat = (p, cat) => p.category === cat || (p.extraCategories && p.extraCategories.includes(cat));
    const base = PRODUCTS.filter(p => {
      if (excludeCat && inCat(p, excludeCat)) return false;
      if (activeCategory && !inCat(p, activeCategory)) return false;
      return true;
    });
    if (!q) return base;
    // Weighted ranking: name match > tag match > description match
    const scored = base.map(p => {
      const name = p.name.toLowerCase();
      const id = p.id.toLowerCase();
      const tags = p.tags.map(t => t.toLowerCase());
      let score = 0;
      if (name.startsWith(q)) score += 100;
      else if (name.includes(q)) score += 60;
      if (id.includes(q)) score += 40;
      if (tags.some(t => t.startsWith(q))) score += 30;
      else if (tags.some(t => t.split(/\s+/).some(w => w.startsWith(q)))) score += 25;
      // Description is the lowest weight, so it only ranks if nothing else matched
      if (score === 0 && p.description.toLowerCase().includes(q)) score = 1;
      return { p, score };
    }).filter(x => x.score > 0);
    // If we have any "strong" hits (name/id/tag), drop description-only fallbacks
    const hasStrong = scored.some(x => x.score >= 15);
    const final = hasStrong ? scored.filter(x => x.score >= 15) : scored;
    final.sort((a, b) => b.score - a.score);
    return final.map(x => x.p);
  }, [activeCategory, searchQuery, PRODUCTS, excludeCat]);

  return (
    <div className="min-h-screen bg-[#020409] text-white">
      {/* TOP UTILITY STRIP */}
      <div className="border-b border-white/5 bg-black/60 backdrop-blur-md text-[11px] uppercase tracking-[0.2em] font-medium">
        <div className="max-w-7xl mx-auto px-6 py-2.5 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-2 text-zinc-500 hover:text-white transition" data-testid="back-to-home">
            <ArrowLeft className="w-3 h-3 text-[#39FF14]" /> {t("nav.back_home")}
          </button>
          <div className="hidden md:flex items-center gap-5 text-zinc-400">
            <button onClick={() => setVerifyOpen(true)} className="flex items-center gap-1.5 hover:text-[#39FF14] transition" data-testid="util-verify">
              <Shield className="w-3.5 h-3.5 text-[#39FF14]" /> {t("util.verify_product")}
            </button>
            <a href="/admin" className="flex items-center gap-1.5 hover:text-white transition" data-testid="util-account">
              <User className="w-3.5 h-3.5" /> {t("util.my_account")}
            </a>
            <button
              onClick={() => navigate("/cart")}
              data-testid="util-cart"
              className="flex items-center gap-1.5 text-zinc-400 hover:text-[#39FF14] transition relative"
            >
              <ShoppingBag className="w-3.5 h-3.5" /> {t("util.cart")}
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-3 w-4 h-4 rounded-full bg-[#39FF14] text-black text-[9px] font-bold flex items-center justify-center">{cartCount}</span>
              )}
            </button>
          </div>
          <button
            onClick={() => navigate("/cart")}
            data-testid="util-cart-mobile"
            className="md:hidden inline-flex items-center gap-1.5 text-zinc-300 hover:text-[#39FF14] transition relative px-2.5 py-1 rounded-md border border-white/10 bg-white/5"
          >
            <ShoppingBag className="w-3.5 h-3.5 text-[#39FF14]" />
            <span className="text-[11px] uppercase tracking-wider font-bold">Cart</span>
            {cartCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-[#39FF14] text-black text-[9px] font-bold flex items-center justify-center">{cartCount}</span>
            )}
          </button>
        </div>
      </div>

      {/* HEADER */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#020409]/85 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-6">
          <button onClick={() => navigate("/")} data-testid="logo"><Logo /></button>
          <div className="relative w-full max-w-md hidden md:block">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
            <Input
              data-testid="products-search-input"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t("prod.header_search_placeholder")}
              className="bg-[#0A0F1A] border-blue-500/30 focus:border-blue-400 focus-visible:ring-blue-500/30 text-white pl-10 pr-9 h-10"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
        {/* Mobile search */}
        <div className="md:hidden px-6 pb-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t("hero.search_placeholder")}
              className="bg-[#0A0F1A] border-blue-500/30 focus:border-blue-400 focus-visible:ring-blue-500/30 text-white pl-10 h-10"
            />
          </div>
        </div>
      </header>

      {/* PAGE HEADING — hidden during search to show results immediately */}
      {!searchQuery.trim() && (
      <section className="py-12 px-6 border-b border-white/5">
        <div className="max-w-7xl mx-auto">
          {(() => {
            const isVials = excludeCat === "pens";
            const activeCatObj = CATEGORIES.find((c) => c.id === activeCategory);
            const title = isVials
              ? t("prod.title_vials")
              : (activeCatObj ? activeCatObj.name.toUpperCase() : t("prod.title_explore"));
            const sub = isVials
              ? t("prod.subtitle_vials")
              : (activeCatObj ? activeCatObj.sub : null);
            return (
              <>
                <p className="text-[11px] uppercase tracking-[0.3em] text-blue-400 font-bold mb-3">
                  {isVials || activeCatObj ? t("prod.eyebrow_category") : t("prod.eyebrow_catalog")}
                </p>
                <h1 className="font-headline text-4xl md:text-5xl font-black tracking-tighter mb-3">
                  {title.split(" ").map((w, i) => (
                    <span key={i}>
                      {i === 0 ? w : <> <span className="text-blue-400 glow-blue">{w}</span></>}
                    </span>
                  ))}
                </h1>
                {sub && activeCategory === "pens" && (
                  <p className="text-zinc-300 text-base max-w-2xl" data-testid="pens-cta-line">
                    {t("prod.pens_cta_pre")}{" "}
                    <a
                      href="mailto:contact@supremelaboratories.com"
                      data-testid="pens-contact-link"
                      className="text-[#39FF14] font-bold hover:underline"
                      style={{ textShadow: "0 0 12px rgba(57,255,20,0.55)" }}
                    >
                      {t("prod.pens_cta_link")}
                    </a>
                  </p>
                )}
                {sub && activeCategory !== "pens" && (
                  <p className="text-zinc-400 text-base max-w-2xl">
                    {sub} · {visibleProducts.length} {t("prod.products_available")}
                  </p>
                )}
                {!isVials && !activeCatObj && (
                  <p className="text-zinc-400 text-base max-w-2xl">
                    {t("prod.subtitle_default", { count: PRODUCTS.length })}
                  </p>
                )}
              </>
            );
          })()}
          <div
            data-testid="products-disclaimer"
            className="mt-5 inline-flex items-start gap-3 rounded-lg border border-red-500/40 bg-red-500/[0.06] px-4 py-2.5"
          >
            <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
            <p className="text-xs leading-relaxed">
              <span className="font-bold uppercase tracking-wider text-red-400">{t("prod.research_only")}</span>
              <span className="text-zinc-300"> · {t("prod.research_only_sub")}</span>
            </p>
          </div>
        </div>
      </section>
      )}

      {/* SEARCH RESULTS HEADER — compact bar shown while searching */}
      {searchQuery.trim() && (
        <section className="py-6 px-6 border-b border-white/5">
          <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <Search className="w-4 h-4 text-[#39FF14] shrink-0" />
              <p className="text-sm text-zinc-300 truncate" data-testid="search-results-line">
                <span className="text-zinc-500">{t("prod.search_results_for")}</span>{" "}
                <span className="text-white font-semibold">"{searchQuery.trim()}"</span>
                <span className="text-zinc-500 ml-2 text-xs">· {visibleProducts.length} {t("prod.search_found")}</span>
              </p>
            </div>
            <button
              type="button"
              onClick={() => setSearchQuery("")}
              data-testid="search-clear"
              className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] font-bold text-zinc-400 hover:text-blue-400 transition border border-white/10 hover:border-blue-500/40 px-3 py-1.5 rounded-md"
            >
              {t("prod.search_clear")} <X className="w-3 h-3" />
            </button>
          </div>
        </section>
      )}

      {/* CATEGORY FILTER STRIP — hidden when a category is already selected or while searching */}
      {!activeCategory && !excludeCat && !searchQuery.trim() && (
      <section className="py-10 px-6 border-b border-white/5">
        <div className="max-w-7xl mx-auto">
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#39FF14] font-bold mb-4">{t("prod.filter_eyebrow")}</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {CATEGORIES.filter(c => c.id !== excludeCat && c.id !== activeCategory).map((cat, idx) => {
              const isActive = activeCategory === cat.id;
              const ringColor = cat.accent === "blue"
                ? "hover:border-blue-500/50 hover:shadow-[0_0_25px_rgba(59,130,246,0.2)]"
                : "hover:border-[#39FF14]/50 hover:shadow-[0_0_25px_rgba(57,255,20,0.15)]";
              return (
                <button
                  key={cat.id}
                  onClick={() => {
                    setActiveCategory(isActive ? null : cat.id);
                    productsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                  data-testid={`filter-category-${cat.id}`}
                  className={`group relative overflow-hidden bg-black border rounded-xl aspect-[2/5] flex flex-col transition-all duration-300 text-left animate-fade-up ${
                    isActive
                      ? "border-[#39FF14] shadow-[0_0_25px_rgba(57,255,20,0.3)]"
                      : `border-white/5 ${ringColor}`
                  }`}
                  style={{ animationDelay: `${idx * 50}ms` }}
                >
                  <div className="relative flex-1 overflow-hidden">
                    <img
                      src={resolveCategoryImage(cat.id, categoryImages)}
                      alt={cat.name}
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="relative bg-black px-3 py-4 min-h-[110px] flex flex-col justify-between">
                    <div
                      className="font-bold uppercase text-white leading-[1.15]"
                      style={{ fontSize: "10.5px", letterSpacing: "0.02em", wordSpacing: "0.05em" }}
                    >
                      {cat.name}
                    </div>
                    <div className="flex items-center justify-between gap-2 mt-2">
                      <span className="text-[9.5px] text-zinc-400 leading-tight" style={{ letterSpacing: "0.01em" }}>{cat.sub}</span>
                      <ArrowRight className={`w-3.5 h-3.5 shrink-0 ${isActive ? "text-[#39FF14]" : "text-[#39FF14]/60"}`} />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>
      )}

      {/* PRODUCT GRID */}
      <section ref={productsRef} className="py-12 px-6 pb-24">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
            <div>
              <p className="text-[11px] uppercase tracking-[0.3em] text-[#39FF14] font-bold mb-2">
                {activeCategory ? CATEGORIES.find(c => c.id === activeCategory)?.sub : t("prod.full_catalog")}
              </p>
              <h3 className="font-headline text-2xl md:text-3xl font-black tracking-tighter text-white">
                {activeCategory ? CATEGORIES.find(c => c.id === activeCategory)?.name : t("prod.all_peptides")}
                <span className="text-zinc-600 font-normal ml-3">({visibleProducts.length})</span>
              </h3>
            </div>
            {activeCategory && (
              <button
                onClick={() => setActiveCategory(null)}
                data-testid="clear-category"
                className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] font-bold text-zinc-400 hover:text-blue-400 transition border border-white/10 hover:border-blue-500/40 px-4 py-2 rounded-md"
              >
                {t("prod.show_all")} <X className="w-3 h-3" />
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {visibleProducts.length === 0 ? (
              <div className="col-span-full text-center py-16 text-zinc-500" data-testid="no-results">
                <Search className="w-10 h-10 mx-auto mb-3 opacity-50" />
                <p className="text-sm">{t("prod.no_results")}</p>
              </div>
            ) : visibleProducts.map((p, idx) => (
              <ProductCard
                key={p.id}
                product={p}
                onAdd={handleAddToCart}
                idx={idx}
                t={t}
                onOpen={() => navigate(`/product/${p.id}`)}
              />
            ))}
          </div>
        </div>
      </section>

      <OrderModal open={modalOpen} onOpenChange={setModalOpen} product={orderProduct} />
      <VerifyDialog open={verifyOpen} onOpenChange={setVerifyOpen} />
    </div>
  );
}

function ProductCard({ product, onAdd, idx, onOpen, t }) {
  return (
    <ProductCardInner product={product} onAdd={onAdd} idx={idx} onOpen={onOpen} t={t} />
  );
}

function CardRating({ productId }) {
  const summary = useReviewSummary(productId);
  if (!summary || !summary.count) return null;
  return (
    <div className="flex items-center gap-1.5 mb-2" data-testid="card-rating">
      <div className="inline-flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((i) => (
          <Star
            key={i}
            className={`w-3 h-3 ${i <= Math.round(summary.avg) ? "text-[#39FF14] fill-[#39FF14]" : "text-zinc-700"}`}
          />
        ))}
      </div>
      <span className="text-[10px] text-zinc-500 tabular-nums">{summary.avg.toFixed(1)} · {summary.count}</span>
    </div>
  );
}

function ProductCardInner({ product, onAdd, idx, onOpen, t }) {
  const photoSrc = photoUrl(product.photo);
  return (
    <article
      data-testid="product-card"
      className="group bg-[#0A0F1A] border border-white/5 hover:border-[#39FF14]/40 rounded-xl overflow-hidden transition-all duration-300 hover:shadow-[0_0_30px_rgba(57,255,20,0.15)] animate-fade-up flex flex-col"
      style={{ animationDelay: `${idx * 40}ms` }}
    >
      <button
        type="button"
        onClick={onOpen}
        data-testid="product-card-open"
        className="relative aspect-square overflow-hidden border-b border-white/5 bg-black flex items-center justify-center cursor-pointer w-full"
        aria-label={`View ${product.name}`}
      >
        <img
          src={photoSrc}
          alt={product.name}
          className="w-full h-full object-contain group-hover:scale-[1.04] transition-transform duration-500"
        />
        <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
          {product.tags.slice(0, 2).map((t) => (
            <Badge key={t} className="bg-black/80 backdrop-blur-md border border-[#39FF14]/20 text-[9px] text-[#39FF14] uppercase tracking-[0.1em] font-bold py-0.5">{t}</Badge>
          ))}
        </div>
        <div className="absolute bottom-3 right-3 bg-black/80 backdrop-blur-md border border-[#39FF14]/30 rounded-md px-2.5 py-1">
          <div className="text-[10px] uppercase tracking-wider text-[#39FF14] font-black">€{product.price}</div>
        </div>
      </button>
      <div className="p-4 flex flex-col flex-1">
        <button
          type="button"
          onClick={onOpen}
          className="text-left hover:text-[#39FF14] transition"
          data-testid="product-card-title"
        >
          <h3 className="font-headline font-bold text-base text-white hover:text-[#39FF14] transition mb-1">{product.name}</h3>
        </button>
        <p className="text-[10px] text-zinc-500 uppercase tracking-[0.1em] mb-2">{product.strength} · {product.volume}</p>
        <CardRating productId={product.id} />
        <div className="flex items-baseline gap-2 mb-3" data-testid="product-card-price">
          <span
            className="font-headline font-black text-3xl text-[#39FF14] tabular-nums"
            style={{ textShadow: "0 0 12px rgba(57,255,20,0.55)" }}
          >
            €{product.price}
          </span>
          <span className="text-[9px] text-zinc-500 uppercase tracking-wider">per unit</span>
        </div>
        <p className="text-xs text-zinc-400 leading-relaxed mb-4 flex-1">{product.description}</p>
        <button
          onClick={() => onAdd(product)}
          data-testid="add-to-cart-btn"
          className="w-full inline-flex items-center justify-center gap-2 bg-[#39FF14] hover:bg-[#2adc0c] text-black text-xs font-bold uppercase tracking-[0.1em] py-3 rounded-md transition shadow-[0_0_12px_rgba(57,255,20,0.35)] hover:shadow-[0_0_20px_rgba(57,255,20,0.55)] mt-auto"
        >
          <ShoppingCart className="w-4 h-4" /> {t("prod.add_to_cart")}
        </button>
      </div>
    </article>
  );
}
